import * as Sentry from '@sentry/react';
import type { AnyRouter } from '@tanstack/react-router';
import { BUILD_VERSION } from './build-info.js';

/**
 * Error, crash and performance reporting, plus the "Report a problem" form.
 *
 * Every Sentry import in the app goes through here. With no DSN, as in local dev and the
 * Playwright run, nothing is initialised and every export is a no-op, so the rest of the
 * app never has to ask whether reporting is on.
 *
 * Mirrors `apps/mobile/src/lib/sentry.ts` deliberately: same shape, different SDK underneath.
 */

const DSN: string | undefined = import.meta.env.VITE_SENTRY_DSN;

export function isSentryConfigured(): boolean {
  return typeof DSN === 'string' && DSN !== '';
}

export function initSentry(router: AnyRouter): void {
  if (!isSentryConfigured()) return;
  Sentry.init({
    dsn: DSN,
    release: BUILD_VERSION,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development',
    // People are identified by their Platewell user id alone, never by email or IP.
    sendDefaultPii: false,
    // Errors raised offline are the ones most worth seeing in an offline-first app, so they
    // wait in IndexedDB until the network is back instead of being dropped.
    transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
    tracesSampleRate: 0.1,
    // A replay is kept only for a session that hit an error, and it shows the layout, never
    // the recipes: every piece of text, every input and every photo is masked.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.tanstackRouterBrowserTracingIntegration(router),
      Sentry.replayIntegration({ maskAllText: true, maskAllInputs: true, blockAllMedia: true }),
      // Opened from Settings rather than as a floating button over every screen.
      Sentry.feedbackIntegration({
        autoInject: false,
        colorScheme: 'system',
        showBranding: false,
        showName: false,
        showEmail: false,
        formTitle: 'Report a problem',
        messagePlaceholder: 'What happened? What did you expect to happen?',
        submitButtonLabel: 'Send report',
        successMessageText: 'Thanks, your report was sent.',
      }),
    ],
  });
}

/** React 19 root error hooks, so render errors caught by any boundary are reported too. */
export const rootErrorHandlers = isSentryConfigured()
  ? {
      onUncaughtError: Sentry.reactErrorHandler(),
      onCaughtError: Sentry.reactErrorHandler(),
      onRecoverableError: Sentry.reactErrorHandler(),
    }
  : {};

export function setSentryUser(id: string | null): void {
  if (!isSentryConfigured()) return;
  Sentry.setUser(id == null ? null : { id });
}

type FeedbackForm = Awaited<
  ReturnType<NonNullable<ReturnType<typeof Sentry.getFeedback>>['createForm']>
>;

/** Built on first use and kept, so opening the form twice does not mount it twice. */
let feedbackForm: Promise<FeedbackForm> | null = null;

export async function openFeedbackForm(): Promise<void> {
  const feedback = Sentry.getFeedback();
  if (feedback == null) return;
  feedbackForm ??= feedback.createForm().then((form) => {
    form.appendToDom();
    return form;
  });
  (await feedbackForm).open();
}

/** A handled failure that should raise a Sentry issue, or reach the console without Sentry. */
export function reportError(message: string, error: unknown): void {
  if (!isSentryConfigured()) {
    console.error(message, error);
    return;
  }
  Sentry.captureException(error, { extra: { message } });
}
